import { FC } from 'react';

import LockIcon from '@/assets/svg/ui-elements/lock.svg';
import { Badge, Switch, theme, Tooltip, Typography } from '@/shared/design-system';
import { CatalogPermission } from '@/shared/modules/role-board/types';
import { PermissionSelection, toPermissionRowState } from '@/utils/permission-catalog';
import type { PendingPermissionChange } from '@/utils/role-request-activity';

import {
  LockedControl,
  PermissionRowBadges,
  PermissionRowControl,
  PermissionRowRoot,
  PermissionRowText,
  PermissionRowTitle,
} from './styled';

export const NOT_GRANTABLE_TOOLTIP =
  'Only an Owner, or a Gamma super admin, can put role administration on a role';
export const LOCKED_TOOLTIP = "Built into this default role and can't be changed";
export const PENDING_LABEL = 'Pending approval';
export const ADDED_LABEL = 'Added';
export const REMOVED_LABEL = 'Removed';
const LOCKED_LABEL = 'Always on';

export const pendingTooltip = ({ granted, initiatorLabel }: PendingPermissionChange) =>
  `${initiatorLabel} asked to ${granted ? 'add' : 'remove'} this. It changes once an approver decides.`;

export type PermissionRowProps = {
  permission: CatalogPermission;
  selection?: PermissionSelection;
  /** False only for a protected permission the caller may not grant. */
  isGrantable: boolean;
  /** False when the caller may look at the board but not change it. */
  editable: boolean;
  /** The staged, unsaved edit on this row: `true` adds it, `false` takes it off. */
  change?: boolean;
  pending?: PendingPermissionChange;
  onChange: (_permission: CatalogPermission, _granted: boolean) => void;
};

export const PermissionRow: FC<PermissionRowProps> = ({
  permission,
  selection,
  isGrantable,
  editable,
  change,
  pending,
  onChange,
}) => {
  const state = toPermissionRowState(selection);

  // A staged removal of a permission the role already holds can always be switched back on: it
  // only undoes the draft and grants nothing new.
  const blocksAdd = !state.on && !isGrantable && change !== false;

  const tooltip = state.locked
    ? LOCKED_TOOLTIP
    : pending
      ? pendingTooltip(pending)
      : blocksAdd
        ? NOT_GRANTABLE_TOOLTIP
        : undefined;

  const control = state.locked ? (
    <LockedControl>
      <LockIcon aria-hidden />
      <Typography variant="body4" color={theme.colors.text.tertiary}>
        {LOCKED_LABEL}
      </Typography>
    </LockedControl>
  ) : (
    <Switch
      size="sm"
      checked={state.on}
      // A second request on the same permission is refused while one is pending.
      disabled={!editable || blocksAdd || Boolean(pending)}
      ariaLabel={permission.label}
      onCheckedChange={(checked) => onChange(permission, checked)}
    />
  );

  return (
    <PermissionRowRoot
      data-permission={permission.permission}
      $change={change === undefined ? null : change ? 'added' : 'removed'}
    >
      <PermissionRowText>
        <PermissionRowTitle>
          <Typography variant="body3" color={theme.colors.text.primary}>
            {permission.label}
          </Typography>
          <PermissionRowBadges>
            {change !== undefined && (
              <Badge
                size="sm"
                variant="subtle"
                color={change ? 'primary' : 'destructive'}
                label={change ? ADDED_LABEL : REMOVED_LABEL}
              />
            )}
            {pending && <Badge size="sm" variant="subtle" color="yellow" label={PENDING_LABEL} />}
            {permission.sensitive && change !== undefined && (
              <Badge size="sm" variant="subtle" color="orange" label="Sensitive" />
            )}
          </PermissionRowBadges>
        </PermissionRowTitle>
        <Typography variant="body4" color={theme.colors.text.tertiary}>
          {permission.description}
        </Typography>
      </PermissionRowText>

      <PermissionRowControl>
        {tooltip ? (
          // A disabled control does not emit pointer events, so the tooltip hangs
          // off a wrapper that does — otherwise the explanation is unreachable
          // exactly when it is needed.
          <Tooltip title={tooltip}>
            <span data-permission-control>{control}</span>
          </Tooltip>
        ) : (
          control
        )}
      </PermissionRowControl>
    </PermissionRowRoot>
  );
};
